import { useAccount } from 'wagmi'
import {
  useELendPositions,
  useEBorrowPositions,
  type ELendPosition,
  type EBorrowPosition,
} from '@/domain/euler/positions'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'

// Re-export types for backward compatibility
export type { ELendPosition, EBorrowPosition }

export interface UseEulerPositionsResult {
  elendPositions: ELendPosition[]
  eborrowPositions: EBorrowPosition[]
  isLoading: boolean
  guestMode: boolean
}

/**
 * Hook to fetch Euler positions (eLend, eBorrow) from onchain
 * Only fetches the positions needed for the portfolio view
 * For eVault positions, use useEVaultPositions directly
 */
export function useEulerPositions(): UseEulerPositionsResult {
  const { address: userAddress } = useAccount()

  const { positions: elendPositions, isLoading: isLoadingELend } = useELendPositions({
    userAddress,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!userAddress,
  })

  const { positions: eborrowPositions, isLoading: isLoadingEBorrow } = useEBorrowPositions({
    userAddress,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!userAddress,
  })

  // Only report loading on initial load (when no data exists yet)
  // This prevents skeleton from showing during background refetches
  const hasData = elendPositions.length > 0 || eborrowPositions.length > 0
  const isLoading = (isLoadingELend || isLoadingEBorrow) && !hasData

  return {
    elendPositions,
    eborrowPositions,
    isLoading,
    guestMode: !userAddress,
  }
}
